'use client';

import { useContext, useMemo } from 'react';

import { Role } from '@ariakit/react';
import cx from 'classnames';

import { Breakpoint } from '~/ui/styles/grid';
import { type ColumnValue } from '~/ui/styles/grid';

import styles from './column.module.scss';
import { ColumnContext, GridContext } from '../context';

/** Represents props used by `<Component.Column />`s */
export type CommonColumnProps = React.ComponentPropsWithoutRef<typeof Role>;
export interface ColumnProps extends CommonColumnProps {
  span?: ColumnValue<number>;
  offsetRight?: ColumnValue<number>;
  offsetLeft?: ColumnValue<number>;
}

export function Column({
  span,
  offsetLeft,
  offsetRight,
  className,
  children,
  style,
  ...rest
}: ColumnProps) {
  const gridContext = useContext(GridContext);
  const columnContext = useContext(ColumnContext);

  // Laptop breakpoint inherits tablet styles if undefined
  // https://github.com/awmf/mellonorg-webapp/issues/2025
  if (span?.tablet && typeof span.laptop === 'undefined') {
    span.laptop = span.tablet;
  }
  if (offsetLeft?.tablet && typeof offsetLeft.laptop === 'undefined') {
    offsetLeft.laptop = offsetLeft.tablet;
  }
  if (offsetRight?.tablet && typeof offsetRight.laptop === 'undefined') {
    offsetRight.laptop = offsetRight.tablet;
  }

  return (
    <Role
      className={cx(
        styles.column,
        {
          [styles.allowOverflow]: gridContext.overflow,
        },
        className,
      )}
      style={{
        ...Object.values(Breakpoint).reduce(
          (style, breakpoint) => ({
            ...style,
            // we don't need to set the CSS variable if it is the same as the parent width because
            //  it will be inherited
            [`--span-${breakpoint}`]:
              span?.[breakpoint] === columnContext[breakpoint] ? undefined : span?.[breakpoint],
            [`--ol-${breakpoint}`]: offsetLeft?.[breakpoint],
            [`--or-${breakpoint}`]: offsetRight?.[breakpoint],
          }),
          {} as React.CSSProperties,
        ),
        ...style,
      }}
      {...rest}
    >
      <ColumnContext.Provider
        value={useMemo(
          () => ({
            [Breakpoint.MOBILE]: span?.[Breakpoint.MOBILE] ?? columnContext[Breakpoint.MOBILE],
            [Breakpoint.TABLET]: span?.[Breakpoint.TABLET] ?? columnContext[Breakpoint.TABLET],
            [Breakpoint.LAPTOP]: span?.[Breakpoint.LAPTOP] ?? columnContext[Breakpoint.LAPTOP],
            [Breakpoint.DESKTOP]: span?.[Breakpoint.DESKTOP] ?? columnContext[Breakpoint.DESKTOP],
          }),
          [columnContext, span],
        )}
      >
        <GridContext.Provider value={{ overflow: false }}>{children}</GridContext.Provider>
      </ColumnContext.Provider>
    </Role>
  );
}
